import { useState } from 'react';

import { api, type Broadcast, type BroadcastPreview } from '../lib/api';
import { Alert, Badge, Card, EnvPill, Field, PageHead, formatTime, useLoader } from '../components/ui';

const STATUS_KIND: Record<string, string> = {
  draft: 'muted',
  scheduled: 'info',
  sending: 'warn',
  sent: 'ok',
  canceled: 'muted',
  failed: 'danger',
};

const STARTER_HTML = `<html><body>
  <h1>Hello {{first_name}}</h1>
  <p>Your update from us.</p>
  <p><a href="{{unsubscribe_url}}">Unsubscribe</a></p>
</body></html>`;

const STARTER_TEXT = `Hello {{first_name}}

Your update from us.

Unsubscribe: {{unsubscribe_url}}`;

/** Section 4.9 / 5.2: the campaign composer and progress view. */
export function Broadcasts({ sendingDomain }: { sendingDomain: string }) {
  const { data, error, loading, reload } = useLoader(() => api.broadcasts.list());
  const audiences = useLoader(() => api.audiences.list());
  const [composing, setComposing] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <>
      <PageHead
        title="Broadcasts"
        subtitle="Campaigns to an audience. Every send carries a one-click unsubscribe header, and skips anyone suppressed or opted out."
        actions={
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setComposing(!composing)}
          >
            {composing ? 'Cancel' : 'New broadcast'}
          </button>
        }
      />

      {error && <Alert kind="error">{error}</Alert>}

      {composing && (
        <Composer
          sendingDomain={sendingDomain}
          audiences={(audiences.data ?? []).map((a) => ({
            id: a.id,
            label: `${a.name} — ${a.subscribed_count} subscribed`,
            subscribed: a.subscribed_count,
          }))}
          onCreated={() => {
            setComposing(false);
            reload();
          }}
        />
      )}

      {(data ?? []).map((broadcast) => (
        <BroadcastCard
          key={broadcast.id}
          broadcast={broadcast}
          open={openId === broadcast.id}
          onToggle={() => setOpenId(openId === broadcast.id ? null : broadcast.id)}
          onChanged={reload}
        />
      ))}

      {(data ?? []).length === 0 && !composing && (
        <Card>
          <div className="empty">
            {loading ? 'Loading…' : 'No broadcasts yet.'}
          </div>
        </Card>
      )}
    </>
  );
}

function Composer({
  sendingDomain,
  audiences,
  onCreated,
}: {
  sendingDomain: string;
  audiences: { id: string; label: string; subscribed: number }[];
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [audienceId, setAudienceId] = useState(audiences[0]?.id ?? '');
  const [from, setFrom] = useState(`news@${sendingDomain}`);
  const [subject, setSubject] = useState('Hello {{first_name}}');
  const [html, setHtml] = useState(STARTER_HTML);
  const [textBody, setTextBody] = useState(STARTER_TEXT);
  const [environment, setEnvironment] = useState('test');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.broadcasts.create({
        name,
        audience_id: audienceId,
        from,
        subject,
        html,
        text: textBody,
        environment,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create broadcast');
    } finally {
      setBusy(false);
    }
  }

  if (audiences.length === 0) {
    return (
      <Card title="New broadcast">
        <Alert kind="warn">
          Create an audience first — a broadcast needs a list to send to.
        </Alert>
      </Card>
    );
  }

  return (
    <Card title="New broadcast">
      {error && <Alert kind="error">{error}</Alert>}
      <form onSubmit={submit}>
        <Field id="b-name" label="Name" hint="Internal only. Recipients never see this.">
          {(props) => (
            <input
              {...props}
              required
              placeholder="March newsletter"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>

        <div className="row" style={{ alignItems: 'flex-start' }}>
          <Field id="b-audience" label="Audience" style={{ flex: '1 1 240px' }}>
            {(props) => (
              <select
                {...props}
                value={audienceId}
                onChange={(event) => setAudienceId(event.target.value)}
              >
                {audiences.map((audience) => (
                  <option key={audience.id} value={audience.id}>
                    {audience.label}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field
            id="b-env"
            label="Environment"
            style={{ flex: '1 1 240px' }}
            hint="Rehearse in test mode first. A campaign cannot be unsent."
          >
            {(props) => (
              <select
                {...props}
                value={environment}
                onChange={(event) => setEnvironment(event.target.value)}
              >
                <option value="test">Test — full pipeline, no mail sent</option>
                <option value="live">Live — sends for real</option>
              </select>
            )}
          </Field>
        </div>

        <Field id="b-from" label="From">
          {(props) => (
            <input
              {...props}
              required
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
          )}
        </Field>

        <Field id="b-subject" label="Subject">
          {(props) => (
            <input
              {...props}
              required
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
            />
          )}
        </Field>

        <Field id="b-html" label="HTML">
          {(props) => (
            <textarea
              {...props}
              style={{ minHeight: 170 }}
              value={html}
              onChange={(event) => setHtml(event.target.value)}
            />
          )}
        </Field>

        <Field id="b-text" label="Plain text">
          {(props) => (
            <textarea
              {...props}
              value={textBody}
              onChange={(event) => setTextBody(event.target.value)}
            />
          )}
        </Field>

        <Alert kind="info">
          <code>{'{{unsubscribe_url}}'}</code> is required in the body — the header
          alone is not enough, since a reader whose client hides it would have no way
          out. <code>{'{{first_name}}'}</code>, <code>{'{{last_name}}'}</code> and{' '}
          <code>{'{{email}}'}</code> are available, plus any custom column from your
          import.
        </Alert>

        <button className="btn btn-primary" disabled={busy}>
          {busy ? 'Saving…' : 'Save draft'}
        </button>
      </form>
    </Card>
  );
}

function BroadcastCard({
  broadcast,
  open,
  onToggle,
  onChanged,
}: {
  broadcast: Broadcast;
  open: boolean;
  onToggle: () => void;
  onChanged: () => void;
}) {
  const [preview, setPreview] = useState<BroadcastPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const recipients = useLoader(
    () => (open ? api.broadcasts.recipients(broadcast.id) : Promise.resolve([])),
    [open, broadcast.status],
  );

  const progress =
    broadcast.total_recipients > 0
      ? Math.round(
          ((broadcast.sent_count + broadcast.skipped_count + broadcast.failed_count) /
            broadcast.total_recipients) *
            100,
        )
      : 0;

  async function act(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title={broadcast.name}
      actions={
        <div className="row">
          <EnvPill environment={broadcast.environment} />
          <Badge kind={STATUS_KIND[broadcast.status] ?? 'muted'}>
            {broadcast.status}
          </Badge>
          {broadcast.status === 'draft' && (
            <>
              <button
                type="button"
                className="btn btn-sm"
                disabled={busy}
                onClick={async () => {
                  setError(null);
                  try {
                    setPreview(await api.broadcasts.preview(broadcast.id));
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Preview failed');
                  }
                }}
              >
                Preview
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                disabled={busy}
                onClick={() => {
                  const confirmation =
                    broadcast.environment === 'live'
                      ? 'Send this campaign for real? It cannot be unsent.'
                      : 'Send in test mode? Nothing will reach a real inbox.';
                  if (!window.confirm(confirmation)) return;
                  void act(() => api.broadcasts.send(broadcast.id));
                }}
              >
                Send now
              </button>
              <button
                type="button"
                className="btn btn-sm btn-danger"
                disabled={busy}
                onClick={() => void act(() => api.broadcasts.cancel(broadcast.id))}
              >
                Cancel
              </button>
            </>
          )}
          {broadcast.status === 'scheduled' && (
            <button
              type="button"
              className="btn btn-sm btn-danger"
              disabled={busy}
              onClick={() => void act(() => api.broadcasts.cancel(broadcast.id))}
            >
              Cancel
            </button>
          )}
          <button type="button" className="btn btn-sm" onClick={onToggle}>
            {open ? 'Hide' : 'Recipients'}
          </button>
        </div>
      }
    >
      {error && <Alert kind="error">{error}</Alert>}
      {broadcast.failure_reason && (
        <Alert kind="error">
          <strong>Halted:</strong> {broadcast.failure_reason}
        </Alert>
      )}

      <div className="row" style={{ gap: 18, marginBottom: 10 }}>
        <span className="hint">
          <strong>{broadcast.subject}</strong>
        </span>
        <span className="spacer" />
        <span className="hint mono">from {broadcast.from_address}</span>
      </div>

      {broadcast.status !== 'draft' && (
        <div className="bar-row" style={{ gridTemplateColumns: '110px 1fr 130px' }}>
          <span>
            {broadcast.status === 'sending' ? 'Sending' : 'Delivered to'}
          </span>
          <span className="bar-track">
            <span className="bar-fill" style={{ width: `${progress}%` }} />
          </span>
          <span className="mono" style={{ textAlign: 'right' }}>
            {broadcast.sent_count}/{broadcast.total_recipients}
            {broadcast.skipped_count > 0 && (
              <span style={{ color: 'var(--warn)' }}> ·{broadcast.skipped_count}</span>
            )}
            {broadcast.failed_count > 0 && (
              <span style={{ color: 'var(--danger)' }}> ·{broadcast.failed_count}</span>
            )}
          </span>
        </div>
      )}

      {broadcast.scheduled_at && (
        <p className="hint">Scheduled for {formatTime(broadcast.scheduled_at)}</p>
      )}
      {broadcast.sent_at && (
        <p className="hint">Completed {formatTime(broadcast.sent_at)}</p>
      )}

      {preview && (
        <div style={{ marginTop: 14 }}>
          {preview.unresolved_placeholders.length > 0 ? (
            <Alert kind="warn">
              These placeholders will be left as literal text — your list has no such
              field: <code>{preview.unresolved_placeholders.join(', ')}</code>
            </Alert>
          ) : (
            <Alert kind="ok">Every placeholder resolves against this audience.</Alert>
          )}
          <div className="hint" style={{ marginBottom: 6 }}>
            Rendered against {preview.sampled_contact ?? 'a sample contact'} — subject:{' '}
            <strong>{preview.subject}</strong>
          </div>
          {/* Sandboxed: this is customer-authored HTML and must not run in the
              dashboard's own document, where it would have our session cookie. */}
          <iframe
            title={`${broadcast.name} preview`}
            sandbox=""
            srcDoc={preview.html ?? ''}
            style={{
              width: '100%',
              height: 220,
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              background: '#fff',
            }}
          />
          <button
            type="button"
            className="btn btn-sm"
            style={{ marginTop: 8 }}
            onClick={() => setPreview(null)}
          >
            Close preview
          </button>
        </div>
      )}

      {open && (
        <div style={{ marginTop: 14 }}>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Reason</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {(recipients.data ?? []).map((row) => (
                  <tr key={row.id}>
                    <td>
                      <Badge
                        kind={
                          row.status === 'sent'
                            ? 'ok'
                            : row.status === 'skipped'
                              ? 'warn'
                              : row.status === 'failed'
                                ? 'danger'
                                : 'muted'
                        }
                      >
                        {row.status}
                      </Badge>
                    </td>
                    <td className="truncate">
                      {row.skip_reason ?? row.failure_reason ?? '—'}
                    </td>
                    <td className="mono truncate">{row.message_id ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(recipients.data ?? []).length === 0 && (
            <div className="empty">
              {recipients.loading ? 'Loading…' : 'No recipients yet.'}
            </div>
          )}
          <p className="hint" style={{ marginTop: 10 }}>
            Skipped means suppressed or unsubscribed — expected in any large list, and
            not counted as a failure.
          </p>
        </div>
      )}
    </Card>
  );
}
